import { Box, CircularProgress } from "@mui/material";
import SavedFilter from "../../../components/SavedFilter";
import useDeleteSavedFilterMutation from "../hooks/useDeleteSavedFilterMutation";
import useUpdateSavedFilterMutation from "../hooks/useUpdateSavedFilterMutation";
import useGetSavedFiltersQuery from "../hooks/useGetSavedFiltersQuery";
import useAuthValue from "../../authModule/hooks/useAuthValue";
import EmptyComponent from "../../../components/EmptyComponent";
import { globalStyles } from "../../../styles/GlobalStyles";
import DeleteFilterModal, {
  toggleDeleteFilterModal,
} from "../../caseManagementModule/components/DeleteFilterModal";
import useUpdateDefaultFilterMutation from "../hooks/useUpdateDefaultFilterMutation";

interface DashboardSavedFiltersTabProps {
  applyFilter: (filter: any, isApplyAction?: boolean) => void;
  startEditingFilterId?: string | null;
  onEditDone?: () => void;
}

const DashboardSavedFiltersTab: React.FC<DashboardSavedFiltersTabProps> = ({
  applyFilter,
  startEditingFilterId = null,
  onEditDone,
}) => {
  const { tenantId, hospitalId } = useAuthValue();
  const { data, isLoading, refetch } = useGetSavedFiltersQuery(
    tenantId,
    hospitalId,
  );

  const { mutate: deleteSavedFilter } = useDeleteSavedFilterMutation(
    tenantId,
    hospitalId,
  );

  const { mutate: updateSavedFilter } = useUpdateSavedFilterMutation(
    tenantId,
    hospitalId,
  );

  const { mutate: updateDefaultFilter } = useUpdateDefaultFilterMutation(
    tenantId,
    hospitalId,
  );

  if (isLoading) {
    return (
      <Box height="200px" sx={globalStyles.colCenter}>
        <CircularProgress />
      </Box>
    );
  }
  const getBoolText = (v: unknown) => {
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (v === "true") return "Yes";
    if (v === "false") return "No";
    return "";
  };
  const filtersToDisplay = data?.map((filter: any) => {
    const autoClosedText = getBoolText(filter.autoClosed);
    const antibioticUsedText = getBoolText(filter.antibioticUsed);
    const inconsistentTimestampsText = getBoolText(filter.inconsistentTimestamps);

    return {
      id: filter.uuid,
      uuid: filter.uuid,
      filterName: filter.filterName,
      dateRange: filter.dateRange,
      otNames: filter.otNames,
      procedureIds: filter.procedureIds,
      primarySurgeons: filter.primarySurgeons,
      circulatingNurses: filter.circulatingNurses,
      anesthesiologists: filter.anesthesiologists,
      caseStartDelays: filter.caseStartDelays,
      caseOrder: filter.caseOrder,
      autoClosed: autoClosedText,
      _rawAutoClosed: filter.autoClosed,
      inconsistentTimestamps: inconsistentTimestampsText,
      _rawInconsistentTimestamps: filter.inconsistentTimestamps,
      antibioticUsed: antibioticUsedText,
      _rawAntibioticUsed: filter.antibioticUsed,
      _rawAdvanceFilters: filter?.advanceFilters,
      caseIds: filter.caseIds,
      isSelected: filter?.isDefault || false,
      advanceFilters: Array.isArray(filter?.advanceFilters)
        ? filter.advanceFilters.map((af: any) => ({
            filterType: af.field,
            condition: af.operator,
            selectedValues: Array.isArray(af.values) ? af.values : [],
          }))
        : [],
    };
  });

  const handleDeleteConfirmed = async (filterId: string) => {
    return new Promise<void>((resolve, reject) => {
      deleteSavedFilter(filterId, {
        onSuccess: () => {
          refetch();
          resolve();
        },
        onError: reject,
      });
    });
  };

  const handleSetDefaultFilter = (filterId: string) => {
    updateDefaultFilter(
      { filterId },
      {
        onSuccess: () => {
          refetch();
        },
      },
    );
  };

  const handleUpdateFilter = (filterId: string, filterName: string) => {
    updateSavedFilter(
      { filterId, filterName },
      {
        onSuccess: () => {
          refetch();
          onEditDone?.();
        },
      },
    );
  };
  if (filtersToDisplay.length === 0) {
    return (
      <Box height="25vh" sx={globalStyles.colCenter}>
        <EmptyComponent title="No_data_to_display" />
      </Box>
    );
  }

  return (
    <>
      <Box>
        {filtersToDisplay.map((filter: any) => (
          <SavedFilter
            key={filter.id}
            filterData={filter}
            applyFilter={(isApplyAction) =>
              applyFilter(
                {
                  ...filter,
                  autoClosed: filter._rawAutoClosed,
                  inconsistentTimestamps: filter._rawInconsistentTimestamps,
                  antibioticUsed: filter._rawAntibioticUsed,
                  advanceFilters: filter._rawAdvanceFilters,
                },
                isApplyAction
              )
            }
            exportFilter={handleUpdateFilter}
            deleteFilter={() =>
              toggleDeleteFilterModal(filter.id, filter.filterName)
            }
            setDefaultFilter={() => handleSetDefaultFilter(filter.id)}
            onChange={() => {}}
            startEditing={filter.id === startEditingFilterId}
          />
        ))}
      </Box>

      <DeleteFilterModal onConfirm={handleDeleteConfirmed} />
    </>
  );
};

export default DashboardSavedFiltersTab;
